import React, { useEffect, useState } from 'react';

import { LayerContentWrapper } from '../components/layer-content-wrapper/LayerContentWrapper';
import { LayerDataBridge } from './layer-data-bridge';

interface BridgeLayerContentProps<T> {
  bridge: LayerDataBridge<T>;
  onClose: () => void;
  getTitle: (data: T) => React.ReactNode;
  renderView: (data: T) => React.ReactNode;
  getOnClose?: (data: T, close: () => void) => (() => void) | undefined;
  getOnGoBack?: (data: T) => (() => void) | undefined;
}

const BridgeLayerContent = <T,>({
  bridge,
  onClose,
  getTitle,
  renderView,
  getOnClose,
  getOnGoBack,
}: BridgeLayerContentProps<T>) => {
  const [data, setData] = useState<T | null>(bridge.getData());

  useEffect(() => {
    return bridge.subscribe(setData);
  }, [bridge]);

  if (!data) return null;

  const resolvedOnClose = getOnClose ? getOnClose(data, onClose) : onClose;

  return (
    <LayerContentWrapper
      title={getTitle(data)}
      onClose={resolvedOnClose ?? (() => {})}
      hideCloseButton={resolvedOnClose === undefined}
      onGoBack={getOnGoBack ? getOnGoBack(data) : undefined}
    >
      {renderView(data)}
    </LayerContentWrapper>
  );
};

export const createBridgeLayerRender = <T,>(
  getTitle: (data: T) => React.ReactNode,
  renderView: (data: T) => React.ReactNode,
  getOnClose?: (data: T, close: () => void) => (() => void) | undefined,
  getOnGoBack?: (data: T) => (() => void) | undefined,
) => (
  state: { dataBridge: LayerDataBridge<T> },
  close: any,
): JSX.Element => (
  <BridgeLayerContent
    bridge={state.dataBridge}
    onClose={() => close()}
    getTitle={getTitle}
    renderView={renderView}
    getOnClose={getOnClose}
    getOnGoBack={getOnGoBack}
  />
);
